import React, { useEffect, useState } from "react";
import Router from "./routes";
import WebFont from "webfontloader";
import { useDispatch, useSelector } from "react-redux";
import styled from "@emotion/styled";
import { Global, css } from "@emotion/core";
import { Toaster } from "react-hot-toast";
import { Header } from "./modules/Header";
import { Sidebar } from "./modules/SideBar/component";
import {
  CustomerProfileEditModal,
  CustomerPasswordFormModal,
  getCustomer,
} from "./modules/Customer";
import { isSignedInSelector } from "ccui-framework";
import { version } from "../package.json";
import SessionExpireWarning from "./components/popups/sessionExpireWarning.jsx";
import SessionExpired from "./components/popups/sessionExpired.jsx";
import {
  CLOSE_ERROR_BOX,
  HIDE_SESSION_EXPIRE_WARNING,
  RESET_EXPIRE,
  SHOW_SESSION_EXPIRED,
} from "./constants/actionTypes";
import SpecificError from "./components/popups/specificError";
import { getMerchants } from "./modules/Merchants/";
import { createSession } from "./modules/Security";

WebFont.load({
  google: {
    families: ["Montserrat:300,400,500,600,700"],
  },
});

const Globals = () => css`
  html,
  body {
    font-family: "Montserrat", sans-serif;
    font-size: 13px;
    width: 100%;
    height: 100%;
  }

  #root {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    background-color: #f0f0f0;
  }

  label {
    font-style: normal;
    font-weight: 600;
    font-size: 10px;
    line-height: 24px;
    text-transform: uppercase;
    color: #777984;
  }

  input[type="text"],
  input[type="password"],
  .form-control {
    min-height: 38px;
    width: 100%;
    box-sizing: border-box;
    font-size: 1rem;
    height: 3rem;
    margin-top: 0.2rem;
    transition: background-color 0.5s ease 0s, border-color 0.5s ease 0s,
      border-width 0.1s ease 0s;
    outline: none;
    color: #3d3d3d;
    align-items: center;
    border-style: none none solid;
    border-bottom: 1px solid rgba(51, 67, 61, 0.1);
    caret-color: #df841f;
    background-color: white;
    margin-bottom: 0.2rem;

    &:focus {
      border-bottom: 1px solid #df841f;
    }
  }

  input {
    font-family: "Montserrat", sans-serif;
  }

  ::placeholder {
    /* Chrome, Firefox, Opera, Safari 10.1+ */
    font-style: normal;
    font-weight: 500;
    font-size: 14px;
    line-height: 24px;
    color: #777984;
  }

  :-ms-input-placeholder {
    /* Internet Explorer 10-11 */
    font-style: normal;
    font-weight: 500;
    font-size: 14px;
    line-height: 24px;
    color: #777984;
  }

  ::-ms-input-placeholder {
    /* Microsoft Edge */
    font-style: normal;
    font-weight: 500;
    font-size: 14px;
    line-height: 24px;
    color: #777984;
  }

  .ReactModal__Content--after-open {
    top: 5rem !important;
    -webkit-transform: scale(1) rotateX(0deg);
    transform: scale(1) rotateX(0deg);
    transition: all 150ms ease-in;
    box-shadow: none !important;
    height: auto !important;
  }

  @media print {
    #header,
    #sideBar,
    #printBtn {
      display: none;
    }
    * {
      color-adjust: exact;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
  }
`;

const Body = styled.div`
  background-color: #ffffff;
  display: flex;
  flex-direction: row;
  height: 100%;
`;

const App = () => {
  console.info("App Version:", version);
  const [fetched, setFetched] = useState(false);
  const isSignedIn = useSelector((state) => isSignedInSelector(state));
  const popups = useSelector((state) => state.popups);
  const dispatch = useDispatch();

  const initializeSession = async () => {
    await dispatch(createSession());
    setFetched(true);
  };

  useEffect(() => {
    initializeSession();
  }, []);

  useEffect(() => {
    if (isSignedIn) dispatch(getMerchants());
  }, [isSignedIn]);

  if (!fetched) return null;

  return (
    <>
      <Global styles={Globals()} />
      <Header />
      <Body>
        <Sidebar />
        <Router.Main />
      </Body>

      <SessionExpired
        showSessionExpired={popups.get("showSessionExpired")}
        startNewSession={() => {
          dispatch(createSession());
        }}
      />
      <SessionExpireWarning
        showSessionExpireWarning={popups.get("showSessionExpireWarning")}
        startNewSession={() => {
          dispatch(createSession());
        }}
        showSessionExpiredBox={() => {
          dispatch({ type: RESET_EXPIRE });
          dispatch({ type: HIDE_SESSION_EXPIRE_WARNING });
          dispatch({ type: SHOW_SESSION_EXPIRED });
          dispatch(getCustomer());
        }}
      />
      {/* temp stuff to make old without new rebranding */}
      <CustomerProfileEditModal />
      <CustomerPasswordFormModal />
      <SpecificError
        notification={popups.get("errorMsg")}
        errorBox={popups.get("errorBox")}
        closeError={() => dispatch({ type: CLOSE_ERROR_BOX })}
        title={popups.get("title")}
      />
      <Toaster />
    </>
  );
};

export { App };
